import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EMPTY, Subject, forkJoin, of } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import {
  CollectorListItem,
  CollectorsConfig
} from '../domain/collector-profile.model';
import { CollectorProfileService } from '../services/collector-profile.service';
import { MyProfileService } from '../services/my-profile.service';
import {
  AVAILABILITY_TAB_QUERY_PARAM,
  AVAILABILITY_TAB_QUERY_VALUE
} from '../services/availability-reminder.constants';

@Component({
  selector: 'app-collector-my-profile',
  templateUrl: './collector-my-profile.component.html',
  styleUrls: ['./collector-my-profile.component.css']
})
export class CollectorMyProfileComponent implements OnInit, OnDestroy {
  collector: CollectorListItem | null = null;
  config: CollectorsConfig | null = null;
  loading = true;
  notFound = false;
  errorMessage: string | null = null;
  selectedTabIndex = 0;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private myProfileService: MyProfileService,
    private collectorProfileService: CollectorProfileService,
    public authService: AuthService,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.selectedTabIndex =
          params.get(AVAILABILITY_TAB_QUERY_PARAM) ===
          AVAILABILITY_TAB_QUERY_VALUE
            ? 1
            : 0;
      });

    forkJoin({
      collector: this.myProfileService.getMyCollector().pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404) {
            this.notFound = true;
            return of(null as unknown as CollectorListItem);
          }
          throw err;
        })
      ),
      config: this.collectorProfileService
        .getConfig()
        .pipe(catchError(() => of(null as unknown as CollectorsConfig)))
    })
      .pipe(
        tap(({ collector, config }) => {
          this.collector = collector ?? null;
          this.config = config ?? null;
        }),
        catchError((err) => {
          this.errorMessage =
            err?.error?.message ?? err?.message ?? 'Could not load profile';
          return EMPTY;
        }),
        finalize(() => {
          this.loading = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get displayName(): string {
    return (
      this.collector?.name ?? this.authService.userProfile?.name ?? 'Collector'
    );
  }

  get displayEmail(): string {
    return this.collector?.email ?? this.authService.userProfile?.email ?? '';
  }

  get hasProfile(): boolean {
    return !!this.collector;
  }
}
